import {Component} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {AnnualSchoolProfilesCardComponent} from "../annual-school-profiles-card/annual-school-profiles-card.component";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {AnnualSchoolProfile} from "../interfaces/annual-school-profile";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {Router, RouterLink} from "@angular/router";
import {Teacher} from "../interfaces/teacher";
import {AnnualSchoolProfileService} from "../services/annual-school-profile.service";
import {SchoolLocations} from "../data/locations";

@Component({
  selector: 'app-annual-school-profiles',
  templateUrl: './annual-school-profiles.component.html',
  standalone: true,
  imports: [
    FormsModule,
    AnnualSchoolProfilesCardComponent,
    NgForOf,
    NgIf,
    RouterLink,
    DatePipe,
  ],
  styleUrl: './annual-school-profiles.component.css'
})
export class AnnualSchoolProfilesComponent {
  constructor(private router: Router, private annualService: AnnualSchoolProfileService) { };
  schoolProfile: AnnualSchoolProfile[] = [];
  profiles: AnnualSchoolProfile[] = [];
  searchValue: string = '';
  private projectMap: Record<string, string> = {};
  teacher: Teacher | null = null;
  downloadedTable = false;
  hoveredRow: number | null = null;

  async ngOnInit() {
    this.teacher = await this.getCurrentTeacher();
    // Fetch profiles from the service
    this.profiles = await this.annualService.getProfiles();
    this.schoolProfile = [...this.profiles];

    // default alphabetical order
    this.sorts = [{ field: 'school', dir: 'asc' }];
    this.applySorting();
    this.updatePagination();
  }

  async getCurrentTeacher(){
    return await this.annualService.getCurrentTeacher();
  }

  onCardClicked(profile: AnnualSchoolProfile) {
    this.router.navigate([`/annual-school-profiles/${profile.id}`]);
  }

  // FILTERED SEARCH
  filter(newSearch: string) {
    this.searchValue = newSearch.toLowerCase().trim();

    if (!this.searchValue) {
      this.schoolProfile = [...this.profiles];
    } else {
      this.schoolProfile = this.profiles.filter(profile =>
        (profile.school?.toLowerCase() ?? '').includes(this.searchValue) ||
        (profile.schoolYear?.toLowerCase() ?? '').includes(this.searchValue) ||
        (profile.ghsRepresentativeName?.toLowerCase() ?? '').includes(this.searchValue)
      );
    }

    this.currentPage = 1;
    this.updatePagination();
    this.downloadedTable = false;
  }

  // PAGINATION
  cardsPerPage = 10;
  currentPage = 1;
  totalPages = 0;
  pages: number[] = [];

  updatePagination() {
    this.totalPages = Math.ceil(this.schoolProfile.length / this.cardsPerPage);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get pagedCards() {
    const start = (this.currentPage - 1) * this.cardsPerPage;
    return this.schoolProfile.slice(start, start + this.cardsPerPage);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPrevious() {
    if (this.currentPage > 1) this.currentPage--;
  }

  goToNext() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  selectChange(event: Event){
    const numberSelected = event.target as HTMLSelectElement;
    if(numberSelected.value !== "All"){
      this.cardsPerPage = Number(numberSelected.value);
    } else {
      this.cardsPerPage = this.schoolProfile.length;
    }
    this.updatePagination();
  }

  profileExportColumns = [
    { header: 'Year', field: 'schoolYear' },
    { header: 'School', field: 'school' },
    { header: 'GHS Rep Name', field: 'ghsRepresentativeName' },
    { header: 'GHS Rep Email', field: 'ghsRepresentativeEmail' },
    { header: 'Teachers', field: 'numOfTeacherAndStaff' },
    { header: 'Students', field: 'numOfStudents' }
  ];

  exportCSV(data: AnnualSchoolProfile[], fileName: string) {
    if (!data?.length) return;
    this.downloadedTable = false;

    const rows: string[] = [];
    rows.push(this.profileExportColumns.map(c => `"${c.header}"`).join(','));

    data.forEach(profile => {
      rows.push(
        this.profileExportColumns
          .map(c => `"${String((profile as any)[c.field] ?? '').replace(/"/g, '""')}"`)
          .join(',')
      );
    });

    const blob = new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
    this.downloadedTable = true;
  }

  sorts: { field: string; dir: 'asc' | 'desc' }[] = [];
  sort(field: string) {
    const current = this.sorts[0];

    if (current?.field === field) {
      current.dir = current.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sorts = [{ field, dir: 'asc' }];
    }
    this.applySorting();
  }

  isEmpty(val: any): boolean {
    return val === null ||
      val === undefined ||
      val === '' ||
      !String(val).trim();
  }

  getClusterForSchool(schoolName?: string): string {
    if (!schoolName) return 'Unknown';
    const location = SchoolLocations.find(s => s.SchoolName === schoolName);
    return location?.Cluster ?? 'Unknown';
  }

  applySorting() {
    this.schoolProfile.sort((a, b) => {
      for (const s of this.sorts) {
        let aVal: any;
        let bVal: any;
        let result = 0;

        switch (s.field) {
          case 'school':
            aVal = a.school?.trim().toLowerCase();
            bVal = b.school?.trim().toLowerCase();
            break;

          case 'year':
            aVal = +(a.schoolYear?.split('-')[0] ?? 0);
            bVal = +(b.schoolYear?.split('-')[0] ?? 0);
            break;

          case 'cluster':
            aVal = this.getClusterForSchool(a.school)?.toLowerCase();
            bVal = this.getClusterForSchool(b.school)?.toLowerCase();
            break;

          case 'ghs':
            aVal = a.ghsRepresentativeName?.trim().toLowerCase();
            bVal = b.ghsRepresentativeName?.trim().toLowerCase();
            break;

          case 'date':
            aVal = new Date(a.submissionDate ?? 0).getTime();
            bVal = new Date(b.submissionDate ?? 0).getTime();
            break;

          case 'updatedDate':
            aVal = new Date(a.editedDate ?? 0).getTime();
            bVal = new Date(b.editedDate ?? 0).getTime();
            break;
        }

        const aEmpty = this.isEmpty(aVal);
        const bEmpty = this.isEmpty(bVal);

        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) continue;

        if (typeof aVal === 'string') {
          result = aVal.localeCompare(bVal);
        } else {
          result = aVal - bVal;
        }

        if (result !== 0) {
          return s.dir === 'asc' ? result : -result;
        }
      }
      return 0;
    });

    this.updatePagination();
  }

  getSortDir(field: string): 'asc' | 'desc' | null {
    const s = this.sorts.find(sort => sort.field === field);
    return s ? s.dir : null;
  }

  showProfile(event: Event) {
    const selection = event.target as HTMLSelectElement;
    const value = selection.value;

    if (value === 'All Profiles') {
      this.schoolProfile = [...this.profiles];
    } else if (value === 'Active') {
      this.schoolProfile = this.profiles.filter(p => !p.isDeleted);
    } else if (value === 'Deleted') {
      this.schoolProfile = this.profiles.filter(p => p.isDeleted);
    }

    this.currentPage = 1;
    this.updatePagination();
  }
}
